import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchCampaignBadgeScanKey, saveCampaignBadgeScanKey } from "../api";
import {
  createCampaignBuilderCampaign,
  fetchCampaignBuilderCampaign,
  fetchCampaignBuilderCampaigns,
  fetchCampaignBuilderConfig,
  fetchCampaignBuilderHealth,
  fetchCampaignTemplatePreview,
  sendCampaignAudience,
  refreshCampaignMetrics,
  completeJourneyStepMappingReview,
  completeJourneyStepTest,
  sendCampaignTest,
  upsertCampaignJourneyStep,
  refreshJourneyStepTemplateAttributes,
  validateJourneyStep,
  type AudiencePreview,
  type CampaignRecipientPlan,
} from "./campaignBuilderApi";
import { AudienceBuilder } from "./AudienceBuilder";
import {
  isJourneyStepTemplateValidated,
  journeyStepDynamicAttributes,
  journeyStepValidationErrors,
} from "./journeyStepValidation";
import { isJourneyStepMappingReviewed } from "./journeyStepMappingReview";
import {
  isJourneyStepTestCommenced,
  isJourneyStepTestCompleted,
  readJourneyStepEmailTesting,
} from "./journeyStepTesting";
import { countByDeliverabilityStatus, deliverabilityLabel, DELIVERABILITY_STATUSES } from "./deliverability";
import { PlannedRecipientsDrawer } from "./PlannedRecipientsDrawer";
import { SendPreviewModal } from "./SendPreviewModal";
import { FieldMappingModal } from "./FieldMappingModal";
import { JourneySendFlow } from "./JourneySendFlow";
import { NewCampaignModal } from "./NewCampaignModal";
import { TestSendModal } from "./TestSendModal";
import { WorkspaceNewButton } from "../components/WorkspaceNewButton";
import { EVENT_KEY } from "../constants";
import type { CampaignCrmNavigation } from "./CrmRecordLinks";
import "./CampaignBuilderScreen.css";

function campaignIdFromPath(pathname: string): string | null {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean);
  if (parts[0] !== "campaigns" || !parts[1]) return null;
  return parts[1];
}

function formatCreatedOn(value: unknown): string {
  if (!value) return "—";
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function campaignPurpose(row: Record<string, unknown>): string {
  const desc = String(row.description ?? "").trim();
  if (desc) return desc;
  return String(row.campaign_type ?? "Marketing campaign");
}

export function CampaignBuilderApp({ crmNavigation }: { crmNavigation?: CampaignCrmNavigation }) {
  const [selectedId, setSelectedId] = useState<string | null>(() => campaignIdFromPath(window.location.pathname));
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [health, setHealth] = useState<Awaited<ReturnType<typeof fetchCampaignBuilderHealth>> | null>(null);
  const [config, setConfig] = useState<Awaited<ReturnType<typeof fetchCampaignBuilderConfig>> | null>(null);
  const [campaigns, setCampaigns] = useState<Record<string, unknown>[]>([]);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [audience, setAudience] = useState<AudiencePreview | null>(null);
  const [activeAudienceFilter, setActiveAudienceFilter] = useState<Record<string, unknown> | null>(null);
  const [templateHtml, setTemplateHtml] = useState<string | null>(null);
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const [sendPreviewStep, setSendPreviewStep] = useState<Record<string, unknown> | null>(null);
  const [newTemplateTitle, setNewTemplateTitle] = useState("");
  const [newTemplateId, setNewTemplateId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refreshingAttributesStepId, setRefreshingAttributesStepId] = useState<string | null>(null);
  const [metricsBusy, setMetricsBusy] = useState(false);
  const [templateIdDrafts, setTemplateIdDrafts] = useState<Record<string, string>>({});
  const [recipientPlan, setRecipientPlan] = useState<CampaignRecipientPlan | null>(null);
  const [plannedDrawerStep, setPlannedDrawerStep] = useState<{ stepId: string; stepName: string } | null>(null);
  const [testSendStep, setTestSendStep] = useState<{
    stepId: string;
    stepName: string;
    dynamicAttributes: string[];
    sendFrom: string;
  } | null>(null);
  const [testSendError, setTestSendError] = useState<string | null>(null);
  const [fieldMappingStep, setFieldMappingStep] = useState<{ stepId: string; stepName: string } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [badgeKeyValue, setBadgeKeyValue] = useState("");
  const [badgeKeyLocked, setBadgeKeyLocked] = useState(false);
  const [badgeKeyLoading, setBadgeKeyLoading] = useState(false);
  const [badgeKeyEditing, setBadgeKeyEditing] = useState(false);
  const [badgeKeyDraft, setBadgeKeyDraft] = useState("");
  const [badgeKeyBusy, setBadgeKeyBusy] = useState(false);
  const [badgeKeyError, setBadgeKeyError] = useState<string | null>(null);

  const navigateCampaign = useCallback((id: string | null) => {
    setSelectedId(id);
    const path = id ? `/campaigns/${id}` : "/campaigns";
    if (window.location.pathname !== path) {
      window.history.pushState({ campaignId: id }, "", path);
    }
  }, []);

  useEffect(() => {
    const onPop = () => setSelectedId(campaignIdFromPath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    void fetchCampaignBuilderHealth().then(setHealth).catch(() => undefined);
    void fetchCampaignBuilderConfig().then(setConfig).catch(() => undefined);
  }, []);

  const reloadCampaigns = useCallback(async () => {
    const data = await fetchCampaignBuilderCampaigns();
    const list = data.results ?? [];
    setCampaigns(list);
    return list;
  }, []);

  useEffect(() => {
    void reloadCampaigns()
      .then((list) => {
        if (!campaignIdFromPath(window.location.pathname) && list.length === 1) {
          navigateCampaign(String(list[0].marketing_campaign_id));
        }
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load campaigns"));
  }, [navigateCampaign, reloadCampaigns]);

  async function onCreateCampaign(values: { name: string; code: string; description: string }) {
    setBusy(true);
    setCreateError(null);
    setError(null);
    try {
      const { campaign } = await createCampaignBuilderCampaign(values);
      const id = String(campaign.marketing_campaign_id ?? "");
      await reloadCampaigns();
      setCreateOpen(false);
      if (id) navigateCampaign(id);
      setMessage(`Created ${values.name}.`);
    } catch (err: unknown) {
      setCreateError(err instanceof Error ? err.message : "Create campaign failed");
    } finally {
      setBusy(false);
    }
  }

  const primaryFilter = useMemo(() => {
    const segments = (detail?.segments as Array<Record<string, unknown>>) ?? [];
    const primary = segments.find((s) => s.is_primary) ?? segments[0];
    return (primary?.filter_json as Record<string, unknown>) ?? null;
  }, [detail]);

  const mergeJourneyStep = useCallback((step: Record<string, unknown>) => {
    setDetail((prev) => {
      if (!prev) return prev;
      const stepId = String(step.step_id);
      const journey_steps = ((prev.journey_steps as Record<string, unknown>[]) ?? []).map((row) =>
        String(row.step_id) === stepId ? step : row,
      );
      return { ...prev, journey_steps };
    });
  }, []);

  const reloadDetail = useCallback(async (id: string) => {
    const data = await fetchCampaignBuilderCampaign(id);
    setDetail(data);
    const plan = data.recipient_plan as CampaignRecipientPlan | undefined;
    setRecipientPlan(plan?.assignments ? plan : null);
    return data;
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setAudience(null);
      setRecipientPlan(null);
      setTemplateHtml(null);
      return;
    }
    setTemplateHtml(null);
    void reloadDetail(selectedId).catch((err: unknown) =>
      setError(err instanceof Error ? err.message : "Failed to load campaign"),
    );
  }, [selectedId, reloadDetail]);

  const campaign = (detail?.campaign as Record<string, unknown>) ?? null;
  const steps = (detail?.journey_steps as Record<string, unknown>[]) ?? [];
  const journeyStepOptions = steps.map((step) => ({
    step_id: String(step.step_id),
    name: String(step.name),
  }));
  const eventCampaignKey = String(campaign?.event_campaign_key ?? EVENT_KEY);

  useEffect(() => {
    if (!selectedId || !campaign) {
      setBadgeKeyValue("");
      setBadgeKeyLocked(false);
      setBadgeKeyDraft("");
      setBadgeKeyEditing(false);
      setBadgeKeyError(null);
      return;
    }
    const campaignKey = String(campaign.event_campaign_key ?? "").trim();
    const campaignName = String(campaign.name ?? "").trim();
    if (!campaignKey) {
      setBadgeKeyValue("");
      setBadgeKeyLocked(false);
      setBadgeKeyDraft("");
      setBadgeKeyEditing(false);
      setBadgeKeyError(null);
      return;
    }
    let cancelled = false;
    setBadgeKeyLoading(true);
    setBadgeKeyError(null);
    void fetchCampaignBadgeScanKey(campaignKey, campaignName)
      .then((row) => {
        if (cancelled) return;
        const key = row.badge_scan_key?.trim() ?? "";
        const locked = row.locked === true && Boolean(key);
        setBadgeKeyValue(key);
        setBadgeKeyLocked(locked);
        setBadgeKeyDraft(key);
        setBadgeKeyEditing(!locked && !key);
      })
      .catch((error) => {
        if (cancelled) return;
        setBadgeKeyError(error instanceof Error ? error.message : "Failed to load badge scanning key");
        setBadgeKeyValue("");
        setBadgeKeyLocked(false);
        setBadgeKeyDraft("");
        setBadgeKeyEditing(true);
      })
      .finally(() => {
        if (!cancelled) setBadgeKeyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [campaign, selectedId]);

  const saveBadgeKey = useCallback(async () => {
    if (!campaign) return;
    const campaignKey = String(campaign.event_campaign_key ?? "").trim();
    const campaignName = String(campaign.name ?? "").trim();
    const key = badgeKeyDraft.trim();
    if (!campaignKey) {
      setBadgeKeyError("Campaign key is missing.");
      return;
    }
    if (!key) {
      setBadgeKeyError("Badge scanning key is required.");
      return;
    }
    setBadgeKeyBusy(true);
    setBadgeKeyError(null);
    try {
      const row = await saveCampaignBadgeScanKey(campaignKey, key, campaignName);
      const savedKey = row.badge_scan_key?.trim() ?? key;
      setBadgeKeyValue(savedKey);
      setBadgeKeyDraft(savedKey);
      setBadgeKeyLocked(row.locked === true && Boolean(savedKey));
      setBadgeKeyEditing(false);
    } catch (error) {
      setBadgeKeyError(error instanceof Error ? error.message : "Failed to save badge scanning key");
    } finally {
      setBadgeKeyBusy(false);
    }
  }, [badgeKeyDraft, campaign]);

  async function applyJourneyStepValidation(templateId: string, stepId: string) {
    const { validation, step } = await validateJourneyStep(stepId);
    mergeJourneyStep(step);
    const errors = Array.isArray(validation.errors) ? validation.errors.map(String) : [];
    if (!validation.ok) {
      setTemplateHtml(null);
      setError(errors.length ? errors.join(" · ") : "Template validation failed");
      return false;
    }
    setError(null);
    const preview = await fetchCampaignTemplatePreview(templateId);
    setTemplateHtml(preview.html ?? null);
    return true;
  }

  async function onValidateJourneyStep(templateId: string, stepId: string) {
    setBusy(true);
    setActiveStepId(stepId);
    try {
      await applyJourneyStepValidation(templateId, stepId);
    } catch (err: unknown) {
      setTemplateHtml(null);
      setError(err instanceof Error ? err.message : "Template validation failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSaveStepTemplateId(step: Record<string, unknown>) {
    if (!selectedId) return;
    const stepId = String(step.step_id);
    const savedId = String(step.sendgrid_template_id ?? "");
    const nextId = (templateIdDrafts[stepId] ?? savedId).trim();
    if (!nextId) {
      setError("SendGrid template ID is required.");
      return;
    }
    if (nextId === savedId) return;
    setBusy(true);
    setActiveStepId(stepId);
    try {
      const { step: updated } = await upsertCampaignJourneyStep({
        step_id: stepId,
        marketing_campaign_id: selectedId,
        name: step.name,
        subject: step.subject ?? step.name,
        sendgrid_template_id: nextId,
        sort_order: step.sort_order,
        active: step.active !== false,
      });
      mergeJourneyStep(updated);
      setTemplateIdDrafts((prev) => {
        const next = { ...prev };
        delete next[stepId];
        return next;
      });
      setTemplateHtml(null);
      setError(null);
      setMessage("Template ID updated. Run Validate when ready.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save template ID failed");
    } finally {
      setBusy(false);
    }
  }

  async function onRefreshTemplateAttributes(stepId: string) {
    setRefreshingAttributesStepId(stepId);
    setActiveStepId(stepId);
    try {
      const { step } = await refreshJourneyStepTemplateAttributes(stepId);
      mergeJourneyStep(step);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Refresh template attributes failed");
    } finally {
      setRefreshingAttributesStepId(null);
    }
  }

  async function onConfirmTestSend(to: string, dynamicTemplateData: Record<string, string>, sendFrom: string) {
    if (!testSendStep) return;
    const { stepId } = testSendStep;
    const audienceFilter = activeAudienceFilter ?? primaryFilter;
    const campaignKey =
      audienceFilter && typeof audienceFilter.campaign_key === "string"
        ? audienceFilter.campaign_key
        : undefined;
    setBusy(true);
    setActiveStepId(stepId);
    setTestSendError(null);
    try {
      const result = await sendCampaignTest(stepId, to, {
        dynamic_template_data: dynamicTemplateData,
        filter_json: audienceFilter ?? undefined,
        campaign_key: campaignKey,
        send_from: sendFrom || undefined,
      });
      if (result.step) mergeJourneyStep(result.step);
      else if (selectedId) await reloadDetail(selectedId);
      setError(null);
      setTestSendError(null);
      setMessage(`Test sent to ${result.actual_to ?? to}.`);
      setTestSendStep(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Test send failed";
      setError(message);
      setTestSendError(message);
    } finally {
      setBusy(false);
    }
  }

  async function onMarkTestCompleted(stepId: string) {
    setBusy(true);
    setActiveStepId(stepId);
    try {
      const { step } = await completeJourneyStepTest(stepId);
      mergeJourneyStep(step);
      setError(null);
      setMessage("Test marked completed — review field mappings next.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not mark test completed");
    } finally {
      setBusy(false);
    }
  }

  async function onMarkMappingReviewed(stepId: string) {
    setBusy(true);
    setActiveStepId(stepId);
    try {
      const { step } = await completeJourneyStepMappingReview(stepId);
      mergeJourneyStep(step);
      setError(null);
      setMessage("Mappings acknowledged — audience send enabled for this email.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not acknowledge mappings");
    } finally {
      setBusy(false);
    }
  }

  async function onRefreshMetrics() {
    if (!selectedId) return;
    setMetricsBusy(true);
    setError(null);
    try {
      const result = await refreshCampaignMetrics(selectedId);
      setRecipientPlan(result.recipient_plan);
      const summary = result.summary;
      setMessage(
        summary.note ??
          `Refresh Metrics updated ${summary.updated} recipient${summary.updated === 1 ? "" : "s"} across ${summary.emails} email${summary.emails === 1 ? "" : "s"}. ${summary.unchanged} already matched SendGrid.`,
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Refresh Metrics failed");
    } finally {
      setMetricsBusy(false);
    }
  }

  async function onConfirmAudienceSend(sendFrom: string) {
    const filter = activeAudienceFilter ?? primaryFilter;
    if (!sendPreviewStep || !filter) return;
    setBusy(true);
    try {
      await sendCampaignAudience(String(sendPreviewStep.step_id), filter, {
        send_from: sendFrom || undefined,
      });
      setSendPreviewStep(null);
      setMessage("Audience send completed.");
      if (selectedId) await reloadDetail(selectedId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Audience send failed");
    } finally {
      setBusy(false);
    }
  }

  async function onAddTemplate() {
    if (!selectedId || !newTemplateTitle.trim()) return;
    const templateId =
      newTemplateId.trim() ||
      config?.templates.mro_prospects ||
      config?.templates.nbaa_general ||
      "";
    if (!templateId) {
      setError("SendGrid template ID is required.");
      return;
    }
    setBusy(true);
    try {
      const { step } = await upsertCampaignJourneyStep({
        marketing_campaign_id: selectedId,
        name: newTemplateTitle.trim(),
        subject: newTemplateTitle.trim(),
        sendgrid_template_id: templateId,
        sort_order: steps.length,
        active: true,
      });
      setNewTemplateTitle("");
      setNewTemplateId("");
      await reloadDetail(selectedId);
      const stepId = String(step.step_id);
      const savedTemplateId = String(step.sendgrid_template_id);
      setActiveStepId(stepId);
      setMessage("Template added — validating SendGrid template…");
      const validated = await applyJourneyStepValidation(savedTemplateId, stepId);
      setMessage(
        validated ? "Template added and validated." : "Template added. Fix the ID or errors, then Validate.",
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Add template failed");
    } finally {
      setBusy(false);
    }
  }

  function openCreate() {
    setCreateError(null);
    setCreateOpen(true);
  }

  const title = campaign ? String(campaign.name) : "";

  return (
    <div className={`campaign-builder-root${navCollapsed ? " nav-collapsed" : ""}`}>
      <div className={`campaign-builder-shell${navCollapsed ? " nav-collapsed" : ""}`}>
      <aside className={`campaign-builder-nav${navCollapsed ? " is-collapsed" : ""}`} aria-label="Campaigns">
          <div className="campaign-builder-nav-head">
            {!navCollapsed ? <h2 className="campaign-builder-nav-title">Campaigns</h2> : null}
            <button
              type="button"
              className="campaign-builder-nav-toggle secondary"
              onClick={() => setNavCollapsed((v) => !v)}
              aria-expanded={!navCollapsed}
              aria-label={navCollapsed ? "Expand campaign list" : "Collapse campaign list"}
              title={navCollapsed ? "Expand list" : "Collapse list"}
            >
              {navCollapsed ? "›" : "‹"}
            </button>
          </div>
          {!navCollapsed ? (
            <ul className="campaign-builder-nav-list">
              {campaigns.map((row) => {
                const id = String(row.marketing_campaign_id);
                const selected = id === selectedId;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={`campaign-nav-item${selected ? " is-selected" : ""}`}
                      onClick={() => navigateCampaign(id)}
                    >
                      <span className="campaign-nav-item-title">{String(row.name)}</span>
                      <span className="campaign-nav-item-purpose">{campaignPurpose(row)}</span>
                      <span className="campaign-nav-item-date">Created {formatCreatedOn(row.created_at)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
      </aside>

        <main className="campaign-builder-main">
          {error ? <p className="campaign-builder-error">{error}</p> : null}
          {message ? <p className="campaign-builder-toast hint">{message}</p> : null}

          <header className="campaign-builder-main-header">
            <div className="campaign-title-row">
              <h1>{title || "Campaigns"}</h1>
              <WorkspaceNewButton onClick={openCreate} />
            </div>
            {campaign?.event_campaign_key || health ? (
              <div className="campaign-title-sub">
                <div className="campaign-title-sub-left">
                  {campaign?.event_campaign_key ? (
                    <p className="hint campaign-builder-code">{String(campaign.event_campaign_key)}</p>
                  ) : null}
                  {badgeKeyError ? <p className="err campaign-builder-badge-key-error">{badgeKeyError}</p> : null}
                </div>
                <div className="campaign-title-sub-right">
                  {campaign?.event_campaign_key ? (
                    <div className="campaign-builder-badge-key">
                      <span className="campaign-builder-badge-key-label">Badge Scanning Key</span>
                      {badgeKeyLoading ? (
                        <span className="campaign-builder-badge-key-loading hint">Loading…</span>
                      ) : badgeKeyLocked ? (
                        <span className="campaign-builder-badge-key-locked" title="Saved and locked for this campaign">
                          {badgeKeyValue}
                        </span>
                      ) : badgeKeyEditing ? (
                        <div className="campaign-builder-badge-key-edit">
                          <input
                            value={badgeKeyDraft}
                            onChange={(e) => setBadgeKeyDraft(e.target.value)}
                            placeholder="Enter badge key"
                            aria-label="Badge scanning key"
                          />
                          <button
                            type="button"
                            onClick={() => void saveBadgeKey()}
                            disabled={badgeKeyBusy || !badgeKeyDraft.trim()}
                          >
                            {badgeKeyBusy ? "Saving…" : "Save"}
                          </button>
                        </div>
                      ) : (
                        <button type="button" className="secondary" onClick={() => setBadgeKeyEditing(true)}>
                          Edit
                        </button>
                      )}
                    </div>
                  ) : null}
                  {health ? (
                    <p className="campaign-builder-meta hint">
                      MCP {health.mcp.ok ? "ok" : "down"} · SendGrid {health.sendgrid.configured ? "ok" : "missing"}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </header>

          {!selectedId || !campaign ? (
            <div className="campaign-builder-empty">
              <p className="hint">Choose a campaign from the list, or use New to add one.</p>
            </div>
          ) : (
            <>

              <section className="campaign-builder-panel" aria-labelledby="audience-builder-heading">
                <div className="campaign-builder-panel-head">
                  <h2 id="audience-builder-heading">Audience Builder</h2>
                  <p className="hint">Define who receives this campaign and preview qualified contacts.</p>
                </div>
                <div className="campaign-builder-panel-body">
                  <AudienceBuilder
                    campaignId={selectedId}
                    campaignKey={eventCampaignKey}
                    initialFilter={primaryFilter}
                    journeySteps={journeyStepOptions}
                    recipientPlan={recipientPlan}
                    onAudienceChange={(preview) => {
                      setAudience(preview);
                      if (preview?.filter) setActiveAudienceFilter(preview.filter);
                    }}
                    onRecipientPlanChange={setRecipientPlan}
                    crmNavigation={crmNavigation}
                  />
                </div>
              </section>

              <section className="campaign-builder-panel" aria-labelledby="campaign-activities-heading">
                <div className="campaign-builder-panel-head campaign-activities-head">
                  <div>
                    <h2 id="campaign-activities-heading">Campaign Activities</h2>
                    <p className="hint">
                      Add SendGrid templates and validate sends. Refresh Metrics loads delivery status for every email.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="secondary campaign-activities-refresh"
                    disabled={metricsBusy || !steps.length}
                    onClick={() => void onRefreshMetrics()}
                  >
                    {metricsBusy ? "Refreshing…" : "Refresh Metrics"}
                  </button>
                </div>
                <div className="campaign-builder-panel-body">
                  <div className="journey-add-template">
                    <h3 className="journey-add-heading">Add template</h3>
                    <div className="journey-add-fields">
                      <label>
                        Template title
                        <input
                          type="text"
                          value={newTemplateTitle}
                          onChange={(e) => setNewTemplateTitle(e.target.value)}
                          placeholder="e.g. MRO follow-up"
                        />
                      </label>
                      <label>
                        SendGrid template ID
                        <input
                          type="text"
                          value={newTemplateId}
                          onChange={(e) => setNewTemplateId(e.target.value)}
                          placeholder={config?.templates.mro_prospects ?? "d-…"}
                        />
                      </label>
                      <button type="button" disabled={busy || !newTemplateTitle.trim()} onClick={() => void onAddTemplate()}>
                        Add template
                      </button>
                    </div>
                  </div>

                  <div className="journey-template-list">
                    {steps.length ? (
                      steps.map((step) => {
                        const stepId = String(step.step_id);
                        const templateId = String(step.sendgrid_template_id);
                        const isActive = activeStepId === stepId;
                        const stepAssignments = recipientPlan?.by_step?.[stepId] ?? [];
                        const planned = stepAssignments.filter((row) => row.deliverability_status === "planned");
                        const statusCounts = countByDeliverabilityStatus(stepAssignments);
                        const trackedStatuses = DELIVERABILITY_STATUSES.filter(
                          (status) => status !== "planned" && statusCounts[status] > 0,
                        );
                        const isValidated = isJourneyStepTemplateValidated(step);
                        const validationErrors = journeyStepValidationErrors(step);
                        const dynamicAttributes = journeyStepDynamicAttributes(step);
                        const attributesRefreshing = refreshingAttributesStepId === stepId;
                        const emailTesting = readJourneyStepEmailTesting(step);
                        const testCommenced = isJourneyStepTestCommenced(step);
                        const testCompleted = isJourneyStepTestCompleted(step);
                        const mappingReviewed = isJourneyStepMappingReviewed(step);
                        const audienceSendEnabled = mappingReviewed;
                        return (
                          <article key={stepId} className={`journey-template-card${isActive ? " is-active" : ""}`}>
                            <div className="journey-template-card-head">
                              <div className="journey-template-card-title">
                                <h3>
                                  <span className="journey-email-icon" aria-hidden="true">✉</span>
                                  {String(step.name)}
                                </h3>
                                {isValidated ? (
                                  <code className="hint">{templateId}</code>
                                ) : (
                                  <div className="journey-template-id-edit">
                                    <label className="hint journey-template-id-label">
                                      SendGrid template ID
                                      <input
                                        type="text"
                                        value={templateIdDrafts[stepId] ?? templateId}
                                        onChange={(e) =>
                                          setTemplateIdDrafts((prev) => ({ ...prev, [stepId]: e.target.value }))
                                        }
                                        placeholder="d-…"
                                        disabled={busy}
                                      />
                                    </label>
                                    {(templateIdDrafts[stepId] ?? templateId).trim() !== templateId ? (
                                      <button
                                        type="button"
                                        className="secondary journey-template-id-save"
                                        disabled={busy}
                                        onClick={() => void onSaveStepTemplateId(step)}
                                      >
                                        Save ID
                                      </button>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                className="secondary journey-planned-open"
                                onClick={() => setPlannedDrawerStep({ stepId, stepName: String(step.name) })}
                              >
                                View list & deliverability
                              </button>
                            </div>
                            <div className="journey-planned-audience">
                              <p className="journey-planned-audience-title">
                                Planned recipients <strong>{planned.length}</strong>
                                {trackedStatuses.map((status) => (
                                  <span key={status} className="hint">
                                    {" "}
                                    · {statusCounts[status]} {deliverabilityLabel(status).toLowerCase()}
                                  </span>
                                ))}
                              </p>
                            </div>
                            {validationErrors.length ? (
                              <p className="journey-validation-errors hint" role="alert">
                                {validationErrors.join(" · ")}
                              </p>
                            ) : null}
                            {dynamicAttributes !== null ? (
                              <div className="journey-dynamic-attributes">
                                <div className="journey-dynamic-attributes-head">
                                  <p className="journey-dynamic-attributes-title">
                                    Dynamic attributes <strong>{dynamicAttributes.length}</strong>
                                  </p>
                                  <button
                                    type="button"
                                    className="secondary journey-dynamic-attributes-refresh"
                                    disabled={busy || attributesRefreshing}
                                    onClick={() => void onRefreshTemplateAttributes(stepId)}
                                  >
                                    {attributesRefreshing ? "Refreshing…" : "Refresh"}
                                  </button>
                                </div>
                                {dynamicAttributes.length ? (
                                  <ul className="journey-dynamic-attributes-list">
                                    {dynamicAttributes.map((name) => (
                                      <li key={name}>
                                        <code className="hint">{name}</code>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="hint journey-dynamic-attributes-empty">
                                    No Handlebars fields found in subject or body.
                                  </p>
                                )}
                              </div>
                            ) : null}
                            {testCommenced && emailTesting.last_test_to ? (
                              <p className="hint journey-test-meta">
                                Last test to <strong>{emailTesting.last_test_to}</strong>
                              </p>
                            ) : (
                              <p className="hint journey-test-meta">
                                Use each button, then check the box below it to unlock the next step.
                              </p>
                            )}
                            <JourneySendFlow
                              isValidated={isValidated}
                              testCompleted={testCompleted}
                              testCommenced={testCommenced}
                              mappingReviewed={mappingReviewed}
                              busy={busy}
                              audienceSendEnabled={audienceSendEnabled}
                              onValidate={() => void onValidateJourneyStep(templateId, stepId)}
                              onTestSend={() => {
                                setTestSendError(null);
                                setTestSendStep({
                                  stepId,
                                  stepName: String(step.name),
                                  dynamicAttributes: dynamicAttributes ?? [],
                                  sendFrom: String(step.send_from ?? ""),
                                });
                              }}
                              onMarkTestCompleted={() => void onMarkTestCompleted(stepId)}
                              onShowMappings={() =>
                                setFieldMappingStep({ stepId, stepName: String(step.name) })
                              }
                              onMarkMappingReviewed={() => void onMarkMappingReviewed(stepId)}
                              onSendAudience={() => setSendPreviewStep(step)}
                            />
                          </article>
                        );
                      })
                    ) : (
                      <p className="hint">No templates yet. Add one above.</p>
                    )}
                  </div>

                  {templateHtml && activeStepId ? (
                    <div className="html-email-preview" dangerouslySetInnerHTML={{ __html: templateHtml }} />
                  ) : null}
                </div>
              </section>
            </>
          )}
        </main>
      </div>

      {plannedDrawerStep && recipientPlan && selectedId ? (
        <PlannedRecipientsDrawer
          open
          campaignId={selectedId}
          stepId={plannedDrawerStep.stepId}
          stepName={plannedDrawerStep.stepName}
          assignments={recipientPlan.by_step[plannedDrawerStep.stepId] ?? []}
          onRecipientPlanChange={setRecipientPlan}
          onClose={() => setPlannedDrawerStep(null)}
          crmNavigation={crmNavigation}
        />
      ) : null}

      {fieldMappingStep ? (
        <FieldMappingModal
          stepId={fieldMappingStep.stepId}
          stepName={fieldMappingStep.stepName}
          audienceFilter={activeAudienceFilter ?? primaryFilter}
          campaignKey={
            (activeAudienceFilter ?? primaryFilter)?.campaign_key as string | undefined
          }
          busy={busy}
          onSaved={(step) => mergeJourneyStep(step)}
          onClose={() => setFieldMappingStep(null)}
          crmNavigation={crmNavigation}
        />
      ) : null}

      {testSendStep && config ? (
        <TestSendModal
          stepName={testSendStep.stepName}
          dynamicAttributes={testSendStep.dynamicAttributes}
          defaultEmail={config.test_email}
          defaultSendFrom={testSendStep.sendFrom}
          error={testSendError}
          busy={busy}
          onConfirm={(to, dynamicTemplateData, sendFrom) =>
            void onConfirmTestSend(to, dynamicTemplateData, sendFrom)
          }
          onClose={() => {
            setTestSendStep(null);
            setTestSendError(null);
          }}
        />
      ) : null}

      {createOpen ? (
        <NewCampaignModal
          busy={busy}
          error={createError}
          onClose={() => {
            if (busy) return;
            setCreateOpen(false);
            setCreateError(null);
          }}
          onSubmit={(values) => void onCreateCampaign(values)}
        />
      ) : null}

      {sendPreviewStep && audience && (activeAudienceFilter ?? primaryFilter) ? (
        <SendPreviewModal
          stepId={String(sendPreviewStep.step_id)}
          stepName={String(sendPreviewStep.name)}
          sendFrom={String(sendPreviewStep.send_from ?? "")}
          audienceFilter={activeAudienceFilter ?? primaryFilter}
          campaignKey={
            (activeAudienceFilter ?? primaryFilter)?.campaign_key as string | undefined
          }
          audience={audience}
          recipientPlan={recipientPlan}
          busy={busy}
          onConfirm={(sendFrom) => void onConfirmAudienceSend(sendFrom)}
          onClose={() => setSendPreviewStep(null)}
          crmNavigation={crmNavigation}
        />
      ) : null}
    </div>
  );
}
